import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserX, Search, AlertCircle, Play, Calendar, Info, ChevronDown, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { usePagination } from '@/hooks/usePagination';

const GOOGLE_ADMIN_USERS_URL = 'https://admin.google.com/ac/users?journey=218';
const BACKUP_MAILBOX = 'backup@winclap.com';

export function InactiveUsersPage() {
  const { t } = useTranslation();
  const [thresholdDays, setThresholdDays] = useState(60);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSteps, setShowSteps] = useState(false);

  // Fetch inactive users
  const { data, isLoading, error, refetch } = trpc.users.getInactivos.useQuery({
    thresholdDays,
  });

  // Manual trigger mutation
  const triggerMutation = trpc.users.triggerAuditoria.useMutation({
    onSuccess: (result) => {
      toast.success(t('users.toasts.audit_success', { count: result.inactiveUsers }));
      refetch();
    },
    onError: (error) => {
      toast.error(t('users.toasts.audit_error', { message: error.message }));
    },
  });

  // Filter users by search query
  const filteredUsers = (data?.inactiveUsers || []).filter((user: any) => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      user.name.toLowerCase().includes(query)
    );
  });
  // Paginado: con umbrales largos la lista trae decenas de cuentas (búsqueda y stats siguen sobre la lista completa)
  const { pageItems: pageUsers, footer: paginationFooter } = usePagination(filteredUsers, 25);

  // Calculate stats
  const stats = {
    total: data?.inactiveCount || 0,
    totalUsers: data?.totalUsers || 0,
    neverLoggedIn: (data?.inactiveUsers || []).filter((u: any) => u.daysSinceLogin === null).length,
  };

  const getDaysSinceBadge = (days: number | null) => {
    if (days === null || days > 10000) {
      return <Badge className="bg-gray-500">{t('users.inactive_page.status.never_logged_in')}</Badge>;
    }
    if (days > 180) {
      return <Badge className="bg-red-500">{t('users.inactive_page.status.days_count', { days })}</Badge>;
    }
    if (days > 90) {
      return <Badge className="bg-orange-500">{t('users.inactive_page.status.days_count', { days })}</Badge>;
    }
    return <Badge className="bg-yellow-500">{t('users.inactive_page.status.days_count', { days })}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('users.inactive_page.title')}</h2>
          <p className="text-muted-foreground">
            {t('users.inactive_page.description')}
          </p>
        </div>
        <Button
          onClick={() => triggerMutation.mutate()}
          disabled={triggerMutation.isPending}
        >
          {triggerMutation.isPending ? (
            <WinclapLoader size={16} className="mr-2" />
          ) : (
            <Play className="mr-2 h-4 w-4" />
          )}
          {t('users.inactive_page.run_audit')}
        </Button>
      </div>

      {/* Aviso: por qué aparecen + cómo dar de baja */}
      <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-5">
        <div className="flex gap-3">
          <Info className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              {t('users.inactive_page.notice.title', { days: thresholdDays })}
            </p>
            <p className="text-amber-900/90 dark:text-amber-200/90">
              {t('users.inactive_page.notice.body')}
            </p>
            <button
              type="button"
              onClick={() => setShowSteps((v) => !v)}
              className="inline-flex items-center gap-1 font-medium text-amber-800 dark:text-amber-300 hover:underline"
            >
              {t('users.inactive_page.notice.toggle')}
              <ChevronDown className={`h-4 w-4 transition-transform ${showSteps ? 'rotate-180' : ''}`} />
            </button>

            {showSteps && (
              <div className="mt-2 space-y-3 rounded-md border border-amber-200 dark:border-amber-800 bg-background/60 p-4">
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{t('users.inactive_page.notice.steps_title')}</p>
                  <a
                    href={GOOGLE_ADMIN_USERS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('users.inactive_page.notice.open_admin')}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm text-foreground/90">
                  <li>{t('users.inactive_page.notice.step1')}</li>
                  <li>{t('users.inactive_page.notice.step2')}</li>
                  <li>{t('users.inactive_page.notice.step3')}</li>
                  <li>{t('users.inactive_page.notice.step4')}</li>
                  <li>
                    {t('users.inactive_page.notice.step5')}{' '}
                    <span className="font-mono font-medium">{BACKUP_MAILBOX}</span>.
                  </li>
                  <li>{t('users.inactive_page.notice.step6')}</li>
                </ol>
                <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {t('users.inactive_page.notice.warning')}
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.inactive_page.stats.inactive_users')}</p>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('users.inactive_page.stats.of_total', { total: stats.totalUsers })}
              </p>
            </div>
            <UserX className="h-8 w-8 text-orange-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.inactive_page.stats.never_logged_in')}</p>
              <p className="text-2xl font-bold">{stats.neverLoggedIn}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('users.inactive_page.stats.inactivity_threshold')}</p>
              <p className="text-2xl font-bold">{thresholdDays} {t('users.inactive_page.stats.days')}</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-500" />
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('users.inactive_page.search_placeholder')}
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={thresholdDays.toString()}
            onValueChange={(v) => setThresholdDays(Number(v))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">{t('users.inactive_page.threshold_options.30')}</SelectItem>
              <SelectItem value="60">{t('users.inactive_page.threshold_options.60')}</SelectItem>
              <SelectItem value="90">{t('users.inactive_page.threshold_options.90')}</SelectItem>
              <SelectItem value="180">{t('users.inactive_page.threshold_options.180')}</SelectItem>
              <SelectItem value="365">{t('users.inactive_page.threshold_options.365')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Data Table */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <WinclapLoader size={48} className="text-foreground/25" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <div className="text-center">
              <p className="font-semibold">{t('users.errors.load_data')}</p>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <UserX className="h-12 w-12 text-muted-foreground" />
            <div className="text-center">
              <p className="font-semibold">
                {searchQuery ? t('users.inactive_page.no_search_results') : t('users.inactive_page.no_inactive')}
              </p>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? t('users.inactive_page.try_different_search')
                  : t('users.inactive_page.all_active', { days: thresholdDays })}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.inactive_page.table.email')}</TableHead>
                <TableHead>{t('users.inactive_page.table.name')}</TableHead>
                <TableHead>{t('users.inactive_page.table.last_login')}</TableHead>
                <TableHead>{t('users.inactive_page.table.days_inactive')}</TableHead>
                <TableHead>{t('users.inactive_page.table.status')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageUsers.map((user: any) => (
                <TableRow key={user.email}>
                  <TableCell className="font-mono text-sm">{user.email}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell className="text-sm">
                    {user.lastLoginTime
                      ? new Date(user.lastLoginTime).toLocaleDateString(currentLocale())
                      : t('users.inactive_page.status.never_logged_in')}
                  </TableCell>
                  <TableCell>{getDaysSinceBadge(user.daysSinceLogin)}</TableCell>
                  <TableCell>
                    {user.suspended ? (
                      <Badge className="bg-red-500">{t('users.inactive_page.status.suspended')}</Badge>
                    ) : (
                      <Badge className="bg-green-500">{t('users.inactive_page.status.active')}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {paginationFooter && <div className="px-4 pb-3">{paginationFooter}</div>}
          </div>
        )}
      </Card>

      {data && (
        <div className="text-sm text-muted-foreground text-center">
          {t('users.inactive_page.last_audit', { date: new Date(data.executedAt).toLocaleString(currentLocale()) })}
        </div>
      )}
    </div>
  );
}
